import { Snackbar, SnackbarCloseReason, Typography } from "@material-ui/core"
import { SyntheticEvent, useMemo, useState } from "react"
import { Alert, Color } from "@material-ui/lab"
import {
  ClientFragment,
  Enum_Safety_Plan_Status_Enum,
  Enum_Safety_Plan_Type_Enum,
  InsertSafetyPlanDocument,
  InsertSafetyPlanMutation,
  InsertSafetyPlanMutationVariables,
  GetPlanByUserIdAndTemplateNameQuery,
} from "../../../generated/graphql"
import { useGraphQLClient } from "../../../hooks/useGraphQLClient"
import {
  RelapsePreventionPlacesByOptionId,
  useRelapsePreventionPlaces,
} from "../../../hooks/useRelapsePreventionPlaces"
import { useSafetyPlanSections } from "../../../hooks/useSafetyPlanSections"
import EditSafetyPlan from "../../molecules/safetyPlan/EditSafetyPlan"
import SafetyPlanCheckList from "../../molecules/safetyPlan/SafetyPlanChecklist"
import {
  buildSelectedOptionsMap,
  ChecklistEntry,
  SelectedSection,
  Section,
  UpdateSafetyPlanOption,
  Selected,
  ConfirmSafetyPlan,
  planTypeToString,
  HydratedChecklistEntry,
} from "../../../utils/safetyPlanUtils"

import SafetyPlanFinishedDialog from "./SafetyPlanFinishedDialog"
import KeepingMyselfSafeChecklist from "./KeepingMyselfSafeChecklist"
import ConfirmSaveButton from "../../molecules/safetyPlan/ConfirmSaveButton"
import SafetyPlanPdfDownloadButton from "../../molecules/safetyPlan/SafetyPlanPdfDownloadButton"
import SafetyPlanSection from "../../molecules/safetyPlan/SafetyPlanSection"

import useSafetyPlanStyles from "./safetyPlan.styles"
import { useUser } from "../../../hooks/useUser"
import { colors } from "../../../utils/theme"

interface SafetyPlanSectionsProps {
  selected: Map<string, SelectedSection>
  checklistOptions: HydratedChecklistEntry[]
  options: Map<string, Section>
  relapsePreventionPlacesByOptionId: RelapsePreventionPlacesByOptionId
  readOnly: boolean
  showSectionDescription: boolean
  handleOpenEdit: (value: string) => void
}

function SafetyPlanSections({
  selected,
  options,
  checklistOptions,
  relapsePreventionPlacesByOptionId,
  readOnly,
  showSectionDescription,
  handleOpenEdit,
}: SafetyPlanSectionsProps) {
  const sectionsArr = selected.size > 0 ? [...selected.values()] : []
  const sections = sectionsArr.map((d: SelectedSection) => {
    // TODO: I don't love hardcoding this, but there's not a good way around this right now
    // since the type is on the category and this is a "section level" difference
    // Consider adjusting the data model to better support these unique section-level type areas of plans
    if (d.header === "Keeping My Environment Safe") {
      return (
        <KeepingMyselfSafeChecklist
          key={d.id}
          selected={d}
          hydratedChecklist={checklistOptions}
        />
      )
    }

    return (
      <SafetyPlanSection
        selected={d}
        options={options}
        relapsePreventionPlacesByOptionId={relapsePreventionPlacesByOptionId}
        handleOpenEdit={handleOpenEdit}
        readOnly={readOnly}
        showSectionDescription={showSectionDescription}
        key={d.id}
      />
    )
  })
  return <>{sections}</>
}

interface SafetyPlanProps {
  client: ClientFragment
  planData: GetPlanByUserIdAndTemplateNameQuery | undefined
  showSectionDescription: boolean
}

/**
 * Renders the editable, practitioner-facing client safety plan experience.
 *
 * If the client has an existing safety plan (excluding rejected plans), it
 * displays:
 * - A summary of the client's responses organised by section
 *   (`SafetyPlanSections`), each of which can be opened for inline editing via
 *   the `EditSafetyPlan` modal.
 * - A PDF download button (`SafetyPlanPdfDownloadButton`) for the current plan.
 * - A `SafetyPlanCheckList` component when the client has indicated they may need
 *   support staying safe (i.e. the radio "Yes" option was selected).
 * - A sticky "Confirm & Save" bar that appears after any edits, which persists
 *   the updated plan to the database with a `Pending` status and notifies the
 *   client. On success the `SafetyPlanFinishedDialog` confirmation is shown; on
 *   failure an error snackbar is displayed.
 *
 * If no safety plan exists yet, renders `SafetyPlanInProgress` to indicate the
 * client hasn't completed their plan in the mobile app.
 *
 * @param client - The client whose safety plan is being viewed/edited.
 * @param readOnly - Specifies whether the plan is in "read only" mode. Used for the relapse prevention plan
 * @param showSectionDescription - Specifies whether to show section descriptions. Currently the relapse prevention plan does not display these
 */
export default function SafetyPlan({
  client,
  planData,
  showSectionDescription,
}: SafetyPlanProps) {
  const classes = useSafetyPlanStyles()
  const { user } = useUser()
  const graphqlClient = useGraphQLClient()

  const plan = planData?.safety_plan_template?.[0].safety_plans?.[0]
  const template = planData?.safety_plan_template[0]
  const templateId = template?.id
  const planType = template?.plan_type
  const sectionData = template?.sections
  const readOnly = template?.is_read_only ?? true

  // State
  const [updatedSafetyPlan, setUpdatedSafetyPlan] = useState<
    UpdateSafetyPlanOption[]
  >([])
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState("")
  const [isVisible, setIsVisible] = useState(false)

  // Show Safety Plan Done
  const [openPlanDone, setOpenPlanDone] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: Color | undefined
    message: string | undefined
  }>({
    open: false,
    severity: undefined,
    message: undefined,
  })

  const clientName = `${client.first_name} ${client.last_name}`

  const clientResponseOptions = useMemo(() => {
    return plan?.responses.selected
  }, [plan])

  const { optionIds, optionsSet } = useMemo(() => {
    const responseIds: string[] = []
    const optionIds: Selected[] = []

    for (const option of clientResponseOptions) {
      optionIds.push({
        id: option.id,
        value: option.custom_values,
        category_type: "",
      })
      responseIds.push(option.id)
    }

    return { optionIds, optionsSet: new Set(responseIds) }
  }, [clientResponseOptions])

  const checklistOptions = useMemo<ChecklistEntry[]>(() => {
    return plan?.responses?.checklist ?? []
  }, [plan])

  const {
    responseOptions,
    selectedOptions,
    setSelectedOptions,
    allOptions,
    hydratedChecklist,
  } = useSafetyPlanSections({
    sectionData,
    optionIds,
    optionsSet,
    checklistEntries: checklistOptions,
  })

  const showChecklist = useMemo(
    () =>
      [...selectedOptions.values()].some((section) =>
        [...section.selectedOptionsMap.values()].some(
          (opt) => opt.category_type === "radio"
        )
      ),
    [selectedOptions]
  )

  const { relapsePreventionPlacesByOptionId } = useRelapsePreventionPlaces(
    client.id,
    planType
  )

  // Handlers / helpers
  const handleOpenEdit = (value: string) => {
    setSelectedId(value)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleCloseSnackbar = (
    event: SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbar({ open: false, severity: undefined, message: undefined })
  }

  /**
   * Receives the flat list of selected options from the EditSafetyPlan modal
   * and rebuilds selectedOptions as a section-keyed map by cross-referencing
   * against allOptions (the full template). Only options present in `data` are
   * placed into each section's selectedOptionsMap, with their custom_values
   * updated from the edited values. Also updates showChecklist based on whether
   * a radio option is present, and makes the Confirm & Save bar visible.
   */
  const save = (data: UpdateSafetyPlanOption[]) => {
    setUpdatedSafetyPlan(data)
    setSelectedOptions(buildSelectedOptionsMap(allOptions, data))
    setIsVisible(true)
  }

  // Save & Confirm
  const confirmSafetyPlan = async () => {
    const selected: ConfirmSafetyPlan[] = updatedSafetyPlan.map((update) => ({
      id: update.id,
      custom_values: update.value,
    }))
    try {
      await graphqlClient.request<
        InsertSafetyPlanMutation,
        InsertSafetyPlanMutationVariables
      >(InsertSafetyPlanDocument, {
        status: Enum_Safety_Plan_Status_Enum.Pending,
        userId: client.id,
        updatedBy: user.id, //practitioner id
        safetyPlanTemplateId: templateId,
        responses: { selected },
      })
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Oops! Something went wrong. Please try again later.",
      })
      return
    }

    setIsVisible(false)
    setOpenPlanDone(true)
  }

  const relapsePreventionPlanIntroText = () => {
    return (
      <p className={classes.planText}>
        Below are the sections of the{" "}
        <strong>ViraPath Relapse Prevention Plan </strong>
        your client created for themselves in the Vira app which they chose to
        share. Please review it carefully, make any edits, and confirm.
      </p>
    )
  }

  // Not strictly necessary IMO, but copilot recommends this guard in order to strictly block rendering
  // if we ever forget to seed a plan / try to render the wrong plan, etc.
  if (!templateId) return null

  return (
    <div id="plan" className={classes.planContainer}>
      <div className={classes.planContent}>
        {planType === Enum_Safety_Plan_Type_Enum.RelapsePrevention ? (
          relapsePreventionPlanIntroText()
        ) : (
          <Typography className={classes.planText}>
            Below is the {planTypeToString(planType, false)} your client created
            in the Vira app. Please review it carefully, make any edits, and
            confirm. Your client will be notified when the plan is edited.
          </Typography>
        )}
      </div>
      <div id="planDetails" className={classes.planDetails}>
        <div className={classes.planDetailsContainer}>
          <Typography
            variant="h2"
            style={{ color: colors.safetyPlan.ink, lineHeight: "40px" }}
          >
            {clientName}'s {planTypeToString(planType, true)}
          </Typography>
          {planType === Enum_Safety_Plan_Type_Enum.Safety && (
            <SafetyPlanPdfDownloadButton
              clientName={clientName}
              selectedOptions={selectedOptions}
            />
          )}
        </div>

        {selectedId && (
          <EditSafetyPlan
            open={open}
            onClose={handleClose}
            data={responseOptions}
            selected={selectedId}
            onSave={save}
            optionIds={optionIds}
          />
        )}

        <SafetyPlanSections
          selected={selectedOptions}
          options={responseOptions}
          checklistOptions={hydratedChecklist}
          relapsePreventionPlacesByOptionId={relapsePreventionPlacesByOptionId}
          handleOpenEdit={handleOpenEdit}
          readOnly={readOnly}
          showSectionDescription={showSectionDescription}
        />

        {showChecklist && (
          <SafetyPlanCheckList name={client.first_name ?? ""} />
        )}
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {isVisible && <ConfirmSaveButton onSave={confirmSafetyPlan} />}
      <SafetyPlanFinishedDialog
        planType={planType}
        open={openPlanDone}
        onClose={() => setOpenPlanDone(false)}
      />
    </div>
  )
}
